import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

const STRATEGIES = [
  { key: "rental", label: "Rental" },
  { key: "fix_flip", label: "Fix & Flip" },
  { key: "buy_hold", label: "Buy & Hold" },
];

const PORTRAITS = [
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&q=80",
  "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300",
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&q=80",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&q=80",
];

export default function Buyers() {
  const [buyers, setBuyers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", buying_areas: "",
    budget_min: "", budget_max: "", cash_available: "",
    closing_speed_days: "", strategy: "fix_flip",
  });

  const load = () => api.get("/buyers").then((r) => setBuyers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    try {
      await api.post("/buyers", {
        ...form,
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
        cash_available: form.cash_available ? Number(form.cash_available) : null,
        closing_speed_days: form.closing_speed_days ? Number(form.closing_speed_days) : null,
        photo_url: PORTRAITS[Math.floor(Math.random() * PORTRAITS.length)],
      });
      setOpen(false);
      setForm({
        name: "", email: "", phone: "", buying_areas: "",
        budget_min: "", budget_max: "", cash_available: "",
        closing_speed_days: "", strategy: "fix_flip",
      });
      toast.success("Buyer added");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this buyer?")) return;
    await api.delete(`/buyers/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="buyers-page">
      <div className="flex items-end justify-between mb-6 reveal">
        <div>
          <span className="app-eyebrow">/ Investors</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">
            Buyer List
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-10 px-5 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]" data-testid="add-buyer-button">
              <Plus size={14} className="mr-2" />
              Add Buyer
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader>
              <DialogTitle>New Buyer</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name</Label>
                <Input
                  data-testid="buyer-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="field-sharp mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="buyer-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="field-sharp mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  data-testid="buyer-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="field-sharp mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Buying Areas</Label>
                <Input
                  data-testid="buyer-areas"
                  value={form.buying_areas}
                  onChange={(e) => setForm({ ...form, buying_areas: e.target.value })}
                  placeholder="Phoenix AZ, Mesa AZ"
                  className="field-sharp mt-1"
                />
              </div>
              <div>
                <Label>Budget Min $</Label>
                <Input
                  data-testid="buyer-budget-min"
                  value={form.budget_min}
                  onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
                  className="field-sharp mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Budget Max $</Label>
                <Input
                  data-testid="buyer-budget-max"
                  value={form.budget_max}
                  onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
                  className="field-sharp mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Cash Available</Label>
                <Input
                  data-testid="buyer-cash"
                  value={form.cash_available}
                  onChange={(e) => setForm({ ...form, cash_available: e.target.value })}
                  className="field-sharp mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Closing Speed (days)</Label>
                <Input
                  data-testid="buyer-closing"
                  value={form.closing_speed_days}
                  onChange={(e) => setForm({ ...form, closing_speed_days: e.target.value })}
                  className="field-sharp mt-1 font-mono"
                />
              </div>
              <div className="col-span-2">
                <Label>Strategy</Label>
                <Select
                  value={form.strategy}
                  onValueChange={(v) => setForm({ ...form, strategy: v })}
                >
                  <SelectTrigger data-testid="buyer-strategy" className="field-sharp mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STRATEGIES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-buyer-button">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {buyers.length === 0 && (
          <div className="card-flat col-span-full p-10 text-center text-muted-foreground">
            No buyers yet — build your cash buyer list to move deals fast.
          </div>
        )}
        {buyers.map((b) => (
          <div key={b.id} className="card-flat p-5 reveal" data-testid={`buyer-${b.id}`}>
            <div className="flex items-start gap-4">
              <img
                src={b.photo_url || PORTRAITS[0]}
                alt={b.name}
                className="w-14 h-14 rounded-sm object-cover"
              />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold">{b.name}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-0.5">
                  {STRATEGIES.find((s) => s.key === b.strategy)?.label || b.strategy}
                </div>
              </div>
              <button
                onClick={() => remove(b.id)}
                className="text-muted-foreground hover:text-destructive"
                data-testid={`del-buyer-${b.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="mt-4 space-y-1.5 text-xs">
              {b.buying_areas && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin size={12} /> {b.buying_areas}
                </div>
              )}
              {b.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone size={12} /> {b.phone}
                </div>
              )}
              {b.email && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail size={12} /> {b.email}
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 pt-4 border-t border-border">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Budget
                </div>
                <div className="font-mono text-sm">
                  {b.budget_min ? money(b.budget_min) : "—"}–
                  {b.budget_max ? money(b.budget_max) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Cash Ready
                </div>
                <div className="font-mono text-sm">
                  {b.cash_available ? money(b.cash_available) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Closes in
                </div>
                <div className="font-mono text-sm">
                  {b.closing_speed_days ? `${b.closing_speed_days}d` : "—"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
